import React, { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import hostHomeBg from "assets/images/others/host-side-images/hostHomeBg.png";
import { ReactComponent as NoEvents } from "assets/images/svgs/no-events.svg";
import { ReactComponent as LocationIcons } from "assets/images/svgs/locationDark.svg";
import { ReactComponent as LocationArrowIcon } from "assets/images/svgs/locationArrowIcon.svg";
import { siteRoutes } from "utils/helpers/enums/routes";
import useStore from "hooks/useStore";
import EventCard from "components/cards/event-card";
import { EventDTO } from "utils/helpers/models/shared/event.dto";
import useHost from "../useHooks";
import ProfileCompletionModal from "./component/ProfileCompletionModal";
import { useSelector } from "react-redux";
import CustomMessageDisplay from "components/data-not-found/CustomMessageDisplay";
import EventNotFoundGif from "assets/images/gifs/event-not-found.gif";
import ProfileNotCompletedGif from "assets/images/gifs/profile-not-completed.gif";


const Home: React.FC = () => {
  const navigate = useNavigate();
  const { getUserFullName } = useStore();
  // const profileCompleted = isProfileCompleted();
  const [upcomingEvents, setUpcomingEvents] = useState<EventDTO[]>([]);
  const [pastEvents, setPastEvents] = useState<EventDTO[]>([]);
  const { getHostHome } = useHost();
  const userFullName = getUserFullName();
  const [filteredUpcomingEvents, setFilteredUpcomingEvents] = useState<any[]>(
    []
  );
  const [currentEventIndex, setCurrentEventIndex] = useState<number>(0);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [hasNextSession, setHasNextSession] = useState<boolean>(false);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const { isProfileCompleted, setProfileModalOpen } = useStore();
  const profileCompleted = isProfileCompleted();
  const profileModalOpen = useSelector(
    (state: any) => state.sharedReducer.profileModalOpen
  );
  const { isLoading } = useSelector((state: any) => state.sharedReducer);

  useEffect(() => {
    // Only show modal if profile is not completed
    if (!profileCompleted) {
      // Check if we're not already on the profile page
      if (window.location.pathname !== "/host-profile") {
        setProfileModalOpen(true);
      }
      
      const interval = setInterval(() => {
        // Re-check profile completion status before showing modal
        const currentProfileCompleted = isProfileCompleted();
        if (!currentProfileCompleted && window.location.pathname !== "/host-profile") {
          setProfileModalOpen(true);
        } else {
          // Profile is completed, close modal and clear interval
          setProfileModalOpen(false);
          clearInterval(interval);
        }
      }, 5 * 60 * 1000);

      return () => clearInterval(interval);
    } else {
      // Profile is completed, ensure modal is closed
      setProfileModalOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileCompleted]);

  // Navigation handlers for "See More" buttons
  const handleUpcommingSeeMore = () => {
    navigate(`${siteRoutes.experiences}?tab=upcoming`);
  };
  const handlePastSeeMore = () => {
    navigate(`${siteRoutes.experiences}?tab=past`);
  };

  const getNextSessionTime = () => {
    if (filteredUpcomingEvents.length > 0) {
      const now = new Date();
      const futureSessions = filteredUpcomingEvents
        .flatMap((event) => event.workshopSessions)
        .map((session: any) => {
          // Handle different date formats
          let date;
          if (session.startDateTime) {
            // If it's already a formatted string, try to parse it
            date = new Date(session.startDateTime);
            // If parsing failed, try with ISO format
            if (isNaN(date.getTime())) {
              date = new Date(session.startDateTime + ':00'); // Add seconds if missing
            }
          }
          return date;
        })
        .filter((date) => date && !isNaN(date.getTime()) && date.getTime() > now.getTime());

      if (futureSessions.length === 0) {
        return null;
      }

      futureSessions.sort((a, b) => a.getTime() - b.getTime());
      return futureSessions[0];
    }
    return null;
  };

  // Countdown timer logic that recalculates every second
  useEffect(() => {
    const interval = setInterval(() => {
      const targetDate = getNextSessionTime();
      if (!targetDate) {
        // No upcoming session exists, clear the timer and reset timeLeft
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setHasNextSession(false);
        clearInterval(interval);
        return;
      }

      setHasNextSession(true);
      const now = new Date();
      const difference = targetDate.getTime() - now.getTime();

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((difference / (1000 * 60)) % 60);
        const seconds = Math.floor((difference / 1000) % 60);
        setTimeLeft({ days, hours, minutes, seconds });
      }
      // When difference becomes less than or equal to 0, the next tick will automatically pick the next session.
    }, 1000);

    return () => clearInterval(interval);
  }, [filteredUpcomingEvents]);

  useEffect(() => {
    if (upcomingEvents && upcomingEvents.length > 0) {
      // Sort events by the earliest start time among all workshop sessions in each event
      const sortedEvents = [...upcomingEvents].sort((a, b) => {
        const aEarliest = Math.min(
          ...a.workshopSessions.map((s: any) => {
            const date = new Date(s.startDateTime);
            return date.getTime();
          })
        );
        const bEarliest = Math.min(
          ...b.workshopSessions.map((s: any) => {
            const date = new Date(s.startDateTime);
            return date.getTime();
          })
        );
        return aEarliest - bEarliest;
      });
      // Take the top three events
      const filtered = sortedEvents.slice(0, 3);
      setFilteredUpcomingEvents(filtered);
      setCurrentEventIndex(0);
      setHasNextSession(false); // Reset session state when events change
    }
  }, [upcomingEvents]);

  useEffect(() => {
    getHostHome(setUpcomingEvents, setPastEvents);
  }, []);

  // Mark initial load as complete after first data load
  useEffect(() => {
    if (isInitialLoad && !isLoading) {
      setIsInitialLoad(false);
    }
  }, [isLoading, upcomingEvents, pastEvents]);

  return (
    <div>
      {/* Header Section */}
      <div
        style={{ backgroundImage: `url(${hostHomeBg})` }}
        className="bg-cover bg-no-repeat h-full md:h-[300px] w-full"
      >
        <div className="bg-gradient h-full">
          <div className="container">
            <div className="flex md:my-10 items-center justify-between gap-4 flex-wrap md:flex-nowrap h-full">
              <div className="flex  flex-col gap-2">
                <p className="text-2xl text-primaryBlueText">Welcome</p>
                <h2 className="md:text-4xl text-3xl font-medium text-primaryBlueText capitalize">
                  {userFullName}
                </h2>
                {profileCompleted ? (
                  <p className="text-[21px] text-primaryBlueText font-medium mt-3">
                    Grow connections and gain visibility in the community.
                  </p>
                ) : (
                  <p className="text-[21px] text-primaryBlueText font-medium mt-3">
                    Complete Profile to Setup an Experience
                  </p>
                )}
                {profileCompleted ? (
                  <button
                    onClick={() => navigate(siteRoutes.createExperience)}
                    className="w-[245px] h-[64px] mt-4 bg-primaryBlueBg rounded-full text-white"
                  >
                    Create an Experience
                  </button>
                ) : (
                  <button
                    onClick={() => navigate(siteRoutes.hostProfile)}
                    className="w-[245px] h-[64px] mt-4 bg-primaryBlueBg rounded-full text-white"
                  >
                    Complete Profile
                  </button>
                )}
              </div>
              <div>
                {profileCompleted && filteredUpcomingEvents.length > 0 && hasNextSession && (
                  <div className="flex flex-col items-start lg:items-center">
                    <h1 className="text-xl font-semibold text-primaryBlueText mb-4">
                      Countdown for Event
                    </h1>
                    <div className="flex space-x-2 sm:space-x-4 mb-4">
                      {["days", "hours", "minutes", "seconds"].map(
                        (unit, index) => (
                          <div
                            key={index}
                            className="flex flex-col items-center justify-center bg-primaryBlueText text-white w-[70px] h-[60px] rounded-md"
                          >
                            <span className="text-base sm:text-2xl font-bold">
                              {String(
                                timeLeft[unit as keyof typeof timeLeft]
                              ).padStart(2, "0")}
                            </span>
                            <span className="text-xs uppercase">
                              {unit}
                            </span>
                          </div>
                        )
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <p
                        onClick={() =>
                          navigate(`${siteRoutes.experienceDetail}?id=${filteredUpcomingEvents[currentEventIndex]?.id}`)
                        }
                        className="text-primaryBlueText text-base cursor-pointer flex gap-2 items-center capitalize"
                      >
                        <LocationIcons />
                        {filteredUpcomingEvents[currentEventIndex]
                          ?.location || ""}
                        <LocationArrowIcon className="" />
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container min-h-[50vh]">
        {profileCompleted ? (
          <div className="mt-5 md:mt-10">
            {/* Show "No Events" message if both upcoming and past are empty */}
            {upcomingEvents?.length === 0 && pastEvents?.length === 0 ? (
              <CustomMessageDisplay
                show={!isLoading && !isInitialLoad}
                title="Let Get Started"
                slogan="It’s quiet for now. Start the excitement by creating an event!"
                image={EventNotFoundGif}
                imageAlt="No events found"
                className="py-16"
                imageClassName="w-[150px] h-[120px]"
                titleClassName="text-2xl font-bold text-blackie"
                sloganClassName="text-gray-500 text-base"
              />
            ) : (
              <>
                {/* Show Upcoming Experiences in first row if available */}
                {upcomingEvents?.length > 0 && (
                  <>
                    <h2 className="text-2xl font-bold mb-4">Upcoming Experiences</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-6">
                      {upcomingEvents.map((event, index) => (
                        <EventCard event={event} key={index} showBookmark={false} />
                      ))}
                    </div>

                    {upcomingEvents?.length > 7 && (
                      <div className="text-center mt-10">
                        <button
                          onClick={handleUpcommingSeeMore}
                          className="px-6 py-2 bg-fade text-primaryBlueText rounded-full"
                        >
                          See More
                        </button>
                      </div>
                    )}
                  </>
                )}

                {/* Show Past Experiences */}
                {pastEvents?.length > 0 && (
                  <div className={upcomingEvents?.length > 0 ? "my-5 md:my-10" : ""}>
                    <h2 className="text-2xl font-bold mb-4">Past Experiences</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 my-6">
                      {pastEvents.map((event, index) => (
                        <EventCard event={event} key={index} showBookmark={false} />
                      ))}
                    </div>

                    {pastEvents?.length > 7 && (
                      <div className="text-center mt-10">
                        <button
                          onClick={handlePastSeeMore}
                          className="px-6 py-2 bg-fade text-primaryBlueText rounded-full"
                        >
                          See More
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="flex flex-col justify-center items-center gap-3 h-[50vh]">
            <span>
              <img src={ProfileNotCompletedGif} alt="No events found" className="w-24 h-24" />
            </span>
            <p className="text-xl font-semibold text-blackie">Complete Your Profile to Get Started</p>
            <p className="text-lightBlackText text-center max-w-md">It’s quiet for now. Start the excitement by Completing Your Profile!</p>
            <button
              onClick={() => navigate(siteRoutes.hostProfile)}
              className="px-6 py-2 bg-fade text-primaryBlueText rounded-full"
            >
              Complete Profile
            </button>
          </div>
        )}
      </div>

      {profileModalOpen && !profileCompleted && <ProfileCompletionModal />}
    </div>
  );
};

export default Home;
